import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getDatabase, ref, get } from 'firebase/database';

export interface ProgramCategory {
  id: string;
  name: string;
  iconImage?: string;
  description: string;
}

interface ProgramCategoriesContextType {
  categories: ProgramCategory[];
  loading: boolean;
  error: string | null;
  refreshCategories: () => void;
  getCategoryById: (id: string) => ProgramCategory | undefined;
}

const ProgramCategoriesContext = createContext<ProgramCategoriesContextType | undefined>(undefined);

export const useProgramCategories = () => {
  const context = useContext(ProgramCategoriesContext);
  if (context === undefined) {
    throw new Error('useProgramCategories must be used within a ProgramCategoriesProvider');
  }
  return context;
};

interface ProgramCategoriesProviderProps {
  children: ReactNode;
}

export const ProgramCategoriesProvider: React.FC<ProgramCategoriesProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<ProgramCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const db = getDatabase();
      const catRef = ref(db, 'programCategory');
      const catSnap = await get(catRef);
      
      if (catSnap.exists()) {
        const catData = catSnap.val();
        const catList: ProgramCategory[] = Object.keys(catData).map((id) => ({
          id,
          name: catData[id].name || id,
          iconImage: catData[id].iconImage || '',
          description: catData[id].description || '',
        }));
        setCategories(catList);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Error fetching program categories:', err);
      setError('Failed to fetch program categories data');
    } finally {
      setLoading(false);
    }
  };

  const refreshCategories = () => {
    fetchCategories();
  };

  const getCategoryById = (id: string): ProgramCategory | undefined => {
    return categories.find(cat => cat.id === id);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const value: ProgramCategoriesContextType = {
    categories,
    loading,
    error,
    refreshCategories,
    getCategoryById,
  };

  return (
    <ProgramCategoriesContext.Provider value={value}>
      {children}
    </ProgramCategoriesContext.Provider>
  );
};

